import { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useState } from 'react';
import { FiLogIn, FiLogOut, FiShoppingCart } from 'react-icons/fi';
import { Link, useLocation } from 'react-router-dom';
import { Tooltip } from 'react-tooltip';
import classNames from 'classnames';
import useIsMobile from 'src/hooks/useIsMobile';
import { Config, ICartState } from '../interfaces';
import MobileMenuOverlayPortal from './MobileMenuOverlayPortal';
import LanguageSelector from './language-selector';
import NavigationLinks from './navigation-links';
import '../styles/top-nav.css';

declare const config: Config;

const pagesWithHiddenElements = ['/center-selection', '/login', '/logout', '/checkout'];

interface Props {
  selectedLanguage: string;
  setSelectedLanguage: Dispatch<SetStateAction<string>>;
  centerId?: string;
  centerName?: string;
  isLoggedIn: boolean;
  cartState?: ICartState;
}

function Navigation({ selectedLanguage, setSelectedLanguage, centerId, centerName, isLoggedIn, cartState }: Props) {
  const [isMobileMenuOpen, setMobileMenuOpen] = useState(false);
  const isMobile = useIsMobile();

  const { pathname } = useLocation();

  const isWithDialog = useMemo(() => pathname.includes('withdialog'), [pathname]);

  const cartItems = useMemo(
    () => ({
      itemsToRender:
        cartState?.items.map((item, key) => (
          <p key={`${item.amount}${item.name}${item.price}-${key}`}>
            {item.amount}x {item.name} - {item.price}
          </p>
        )) ?? [],
      amount: cartState?.items.reduce((sum, { amount }) => sum + amount, 0) ?? 0,
    }),
    [cartState],
  );

  const hideMenu = useCallback(() => {
    setMobileMenuOpen(false);
    document.body.classList.remove('no-scroll');
    document.documentElement.classList.remove('no-scroll');
  }, []);

  const topNav = useMemo(() => {
    const { itemsToRender, amount: cartItemsAmount } = cartItems;

    if (isMobile) {
      const toggleMenu = () => {
        setMobileMenuOpen((prev) => {
          if (!prev) {
            document.body.classList.add('no-scroll');
            document.documentElement.classList.add('no-scroll');
          } else {
            document.body.classList.remove('no-scroll');
            document.documentElement.classList.remove('no-scroll');
          }

          return !prev;
        });
      };

      return (
        <div className='mobile-navbar'>
          <div className='mobile-center-name'>
            <Link to={isWithDialog ? '/withdialog' : '/'} className='center-name-link' onClick={hideMenu}>
              {centerName ? (isWithDialog ? `Compeso Dialog Mock(${centerName})` : centerName) : null}
            </Link>
          </div>
          <div className='mobile-nav-actions'>
            {!pagesWithHiddenElements.includes(pathname) && (
              <>
                <Link
                  to={`/center/${centerId}${isWithDialog ? '/withdialog' : ''}/checkout`}
                  className='cart-icon'
                  onClick={hideMenu}
                >
                  <FiShoppingCart />
                  {cartItemsAmount > 0 && <span className='cart-badge'>{cartItemsAmount}</span>}
                </Link>
                {isLoggedIn ? (
                  <Link
                    to={`/center/${centerId}${isWithDialog ? '/withdialog' : ''}/logout`}
                    className='login-icon'
                    onClick={hideMenu}
                  >
                    <FiLogOut />
                  </Link>
                ) : (
                  <Link
                    to={`/center/${centerId}${isWithDialog ? '/withdialog' : ''}/login`}
                    className='login-icon'
                    onClick={hideMenu}
                  >
                    <FiLogIn />
                  </Link>
                )}
              </>
            )}
            {pathname !== '/center-selection' && (
              <div className={classNames('mobile-menu-button', { open: isMobileMenuOpen })} onClick={toggleMenu}>
                {isMobileMenuOpen ? <span className='fa fa-times' /> : <span className='fa fa-bars' />}
              </div>
            )}
          </div>
        </div>
      );
    }

    return (
      <>
        <div className={classNames('desktop-navbar-top', { 'with-dialog': isWithDialog })}>
          <div className='center-name'>
            <Link to={isWithDialog ? '/withdialog' : '/'} className='center-name-link' onClick={hideMenu}>
              {centerName ? (isWithDialog ? `Compeso Dialog Mock(${centerName})` : centerName) : null}
            </Link>
          </div>
          <div className='nav-selectors'>
            {!pagesWithHiddenElements.includes(pathname) && (
              <>
                <Link
                  to={`/center/${centerId}${isWithDialog ? '/withdialog' : ''}/checkout`}
                  className='cart-link'
                  onClick={hideMenu}
                >
                  Cart <FiShoppingCart />
                  {cartItemsAmount > 0 && <span className='cart-badge'>{cartItemsAmount}</span>}
                </Link>
                {isLoggedIn ? (
                  <Link to={`/center/${centerId}${isWithDialog ? '/withdialog' : ''}/logout`}>
                    Logout <FiLogOut />
                  </Link>
                ) : (
                  <Link to={`/center/${centerId}${isWithDialog ? '/withdialog' : ''}/login`}>
                    Login <FiLogIn />
                  </Link>
                )}
              </>
            )}
            <Tooltip float anchorSelect='.cart-link'>
              {itemsToRender.length === 0 ? <p>No items added to cart</p> : itemsToRender}
            </Tooltip>
            <LanguageSelector selectedLanguage={selectedLanguage} setSelectedLanguage={setSelectedLanguage} />
            {!pagesWithHiddenElements.includes(pathname) && (
              <div className='center-selection'>
                <Link to={`${isWithDialog ? '/withdialog' : ''}/center-selection`} className='center-link'>
                  <span className='fa fa-map-marker' aria-hidden='true' />
                </Link>
              </div>
            )}
          </div>
        </div>
        <div className={classNames('desktop-navbar-bottom', { 'with-dialog': isWithDialog })}>
          <div className='navigation-links-container'>
            <NavigationLinks
              config={config}
              selectedLanguage={selectedLanguage}
              centerId={centerId}
              withDialog={isWithDialog}
              hideMenu={hideMenu}
            />
          </div>
        </div>
      </>
    );
  }, [
    cartItems,
    centerId,
    centerName,
    hideMenu,
    isLoggedIn,
    isMobile,
    isMobileMenuOpen,
    isWithDialog,
    pathname,
    selectedLanguage,
    setSelectedLanguage,
  ]);

  useEffect(() => {
    if (isWithDialog) {
      document.body.classList.add('with-dialog');
    } else {
      document.body.classList.remove('with-dialog');
    }
  }, [isWithDialog]);

  if (pathname.includes('center-selection')) {
    return null;
  }

  return (
    <div className='navigation-container'>
      {topNav}
      {pathname !== '/center-selection' && isMobile && (
        <MobileMenuOverlayPortal isOpen={isMobileMenuOpen} withDialog={isWithDialog}>
          <button className='mobile-menu-close-button' onClick={hideMenu}>
            <span className='fa fa-times' />
          </button>
          <div className='mobile-extra-menu'>
            <LanguageSelector selectedLanguage={selectedLanguage} setSelectedLanguage={setSelectedLanguage} />
            {!pagesWithHiddenElements.includes(pathname) && (
              <Link
                to={`${isWithDialog ? '/withdialog' : ''}/center-selection`}
                className='center-selection-mobile'
                onClick={hideMenu}
              >
                Center selection
              </Link>
            )}
          </div>
          <NavigationLinks
            config={config}
            selectedLanguage={selectedLanguage}
            centerId={centerId}
            withDialog={isWithDialog}
            hideMenu={hideMenu}
          />
        </MobileMenuOverlayPortal>
      )}
    </div>
  );
}

export default Navigation;
